document.addEventListener('DOMContentLoaded', () => {
  /* -----------------------------------------------------
     MOBILE DRAWER - with aria sync + backdrop class fix
     ----------------------------------------------------- */
  const menuButton = document.querySelector('[data-mobile-menu-open]');
  const drawer = document.querySelector('[data-mobile-drawer]');
  const backdrop = document.querySelector('[data-mobile-backdrop]');
  const drawerClose = document.querySelector('[data-mobile-menu-close]');

  /* An open drawer is a modal surface: focus must not be able to leave it.
     Without a trap, Tab walks straight out of the drawer and into the page
     behind it, where the user is operating controls they cannot see. */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const drawerFocusables = () =>
    drawer ? Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), (el) => el.offsetParent !== null) : [];

  /* Everything outside the drawer is hidden from assistive tech while it is
     open, so a screen reader cannot wander into the page underneath either. */
  /* Hide every top-level region except the drawer itself. The previous
     predicate excluded any element CONTAINING the drawer, which is the header
     — precisely the region a screen-reader user would wander into first. The
     drawer is re-parented conceptually by hiding its siblings and, within its
     own ancestor chain, hiding that ancestor's other children. */
  const backgroundRegions = () => {
    const regions = [];
    let node = drawer;
    while (node && node.parentElement && node !== document.body) {
      Array.prototype.forEach.call(node.parentElement.children, (sib) => {
        if (sib !== node && sib !== backdrop) regions.push(sib);
      });
      node = node.parentElement;
    }
    return regions;
  };

  const setBackgroundHidden = (hidden) => {
    backgroundRegions().forEach((el) => {
      if (hidden) {
        if (el.hasAttribute('aria-hidden')) el.dataset.sandyAriaWas = el.getAttribute('aria-hidden');
        el.setAttribute('aria-hidden', 'true');
      } else if ('sandyAriaWas' in el.dataset) {
        el.setAttribute('aria-hidden', el.dataset.sandyAriaWas);
        delete el.dataset.sandyAriaWas;
      } else {
        el.removeAttribute('aria-hidden');
      }
    });
  };

  const toggleDrawer = (open) => {
    if (!drawer || !backdrop) return;
    drawer.classList.toggle('is-open', open);
    backdrop.classList.toggle('is-visible', open);
    document.body.style.overflow = open ? 'hidden' : '';
    if (menuButton) menuButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    setBackgroundHidden(open);
    if (open) {
      const closeBtn = drawer.querySelector('[data-mobile-menu-close]');
      if (closeBtn) closeBtn.focus();
    } else if (menuButton) {
      /* Restore unconditionally. Guarding this on "focus is still inside the
         drawer" fails in exactly the case that needs recovery most — focus
         having already escaped — leaving the user stranded in background
         content with no way back. */
      menuButton.focus();
    }
  };

  /* Crossing the breakpoint hides the toggle. Without this the drawer's modal
     state (scroll lock + background aria-hidden) persisted at desktop width
     with no visible control to dismiss it. */
  const drawerMq = window.matchMedia('(min-width: 990px)');
  const onBreakpoint = (e) => { if (e.matches && drawer && drawer.classList.contains('is-open')) toggleDrawer(false); };
  if (drawerMq.addEventListener) drawerMq.addEventListener('change', onBreakpoint);
  else if (drawerMq.addListener) drawerMq.addListener(onBreakpoint);

  if (menuButton) menuButton.addEventListener('click', () => toggleDrawer(true));
  if (drawerClose) drawerClose.addEventListener('click', () => toggleDrawer(false));
  if (backdrop) backdrop.addEventListener('click', () => toggleDrawer(false));
  document.addEventListener('keydown', (e) => {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') { toggleDrawer(false); return; }
    if (e.key !== 'Tab') return;
    const items = drawerFocusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    /* Focus outside the drawer entirely (e.g. after a click on the page)
       is pulled back in rather than allowed to continue escaping. */
    if (!drawer.contains(document.activeElement)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* -----------------------------------------------------
     SCROLL-AWARE HEADER
     ----------------------------------------------------- */
  const header = document.querySelector('.header');
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          header.classList.toggle('is-scrolled', window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -----------------------------------------------------
     PRODUCT PAGE - quantity + gallery (unchanged)
     ----------------------------------------------------- */
  document.querySelectorAll('[data-quantity-button]').forEach((button) => {
    button.addEventListener('click', () => {
      const wrap = button.closest('[data-quantity-wrap]');
      const input = wrap?.querySelector('input[name="quantity"], input[name="updates[]"], input[type="number"]');
      if (!input) return;
      const current = parseInt(input.value || '1', 10);
      const min = parseInt(input.getAttribute('min') || '1', 10);
      const next = button.dataset.quantityButton === 'plus' ? current + 1 : Math.max(min, current - 1);
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-image-src');
        if (!main || !src) return;
        main.setAttribute('src', src);
        const srcset = thumb.getAttribute('data-image-srcset');
        if (srcset) main.setAttribute('srcset', srcset);
        const alt = thumb.getAttribute('data-image-alt') || '';
        main.setAttribute('alt', alt);
        thumbs.forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  });

  /* -----------------------------------------------------
     PRODUCT FORM - variant selection sync
     ----------------------------------------------------- */
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    let variants = [];
    try { variants = JSON.parse(form.dataset.productVariants || '[]'); } catch (e) { variants = []; }
    if (!variants.length) return;

    /* Option controls are now radio inputs so the selected value carries a
       boundary and a check mark rather than living inside a dropdown. A
       <select> carrying the same attribute still works, so a section that
       has not migrated keeps functioning. Both report their option index
       through data-option-position, and the chosen combination is read
       positionally either way. */
    const controls = form.querySelectorAll('[data-variant-select]');
    const chosenValues = () => {
      const byPos = [];
      Array.prototype.forEach.call(controls, (el) => {
        const pos = parseInt(el.dataset.optionPosition || '0', 10);
        if (el.tagName === 'SELECT') byPos[pos] = el.value;
        else if (el.checked) byPos[pos] = el.value;
      });
      return byPos;
    };
    const hiddenId = form.querySelector('[data-variant-id-input]');
    /* The hidden id field ships disabled so that, without scripting, it
       cannot post a stale variant while the customer believes they chose
       another. Now that this script is running and will keep it in sync,
       it becomes the field that carries the selection. */
    if (hiddenId) hiddenId.disabled = false;
    const priceEl = document.querySelector('[data-variant-price]');
    const stickyPriceEl = document.querySelector('[data-sticky-price]');
    const stickyBtn = document.querySelector('[data-sticky-atc-btn]');
    const stockBadge = document.querySelector('[data-stock-badge]');
    const stockCountEl = stockBadge ? stockBadge.querySelector('[data-stock-count]') : null;
    const stockThreshold = stockBadge ? parseInt(stockBadge.dataset.lowStockThreshold || '0', 10) : 0;
    const stockShowUntracked = stockBadge ? stockBadge.dataset.lowStockShowWhenUntracked === '1' : false;
    const submitBtn = form.querySelector('button[type="submit"]');

    /* The server already rendered a price through Liquid's currency-aware
       money filter. Hardcoding "£" here would overwrite that with the wrong
       symbol and grouping for any other currency, so mirror whatever the
       server produced: reuse its non-numeric characters and only swap the
       number. Falls back to the raw amount rather than inventing a currency. */
    const priceTemplate = (priceEl && priceEl.textContent.trim()) || '';
    const formatMoney = (cents) => {
      const v = cents / 100;
      const num = Number.isInteger(v) ? String(v) : v.toFixed(2);
      const m = priceTemplate.match(/^([^\d]*)[\d.,\s]+([^\d]*)$/);
      return m ? `${m[1]}${num}${m[2]}` : num;
    };

    const syncStock = (match) => {
      if (!stockBadge) return;
      const tracked = match.inventory_management === 'shopify';
      const qty = (typeof match.inventory_quantity === 'number') ? match.inventory_quantity : null;
      let show = false;
      if (tracked && qty !== null && qty > 0 && qty <= stockThreshold) {
        show = true;
        if (stockCountEl) stockCountEl.textContent = qty;
      } else if (!tracked && stockShowUntracked) {
        show = true;
      }
      stockBadge.hidden = !show;
    };

    /* One place drives the CTA state for BOTH the main submit and the
       sticky button. `data-original-text` is server-rendered (with the
       price suffix on the main button), so a server-rendered "Sold out"
       initial state is never cached as the "original" label. */
    const setCtaState = (state, priceStr) => {
      const offLabel = state === 'soldout' ? 'Sold out' : 'Unavailable';
      if (submitBtn) {
        if (!submitBtn.dataset.originalText) submitBtn.dataset.originalText = submitBtn.textContent.trim();
        submitBtn.disabled = state !== 'available';
        if (state === 'available') {
          let priceSpan = submitBtn.querySelector('[data-cta-price]');
          if (!priceSpan) {
            // Rebuild "Label — <price>" (the span is lost while showing Sold out/Unavailable).
            const base = submitBtn.dataset.originalText.replace(/\s*—[^—]*$/, '');
            submitBtn.textContent = base + ' — ';
            priceSpan = document.createElement('span');
            priceSpan.setAttribute('data-cta-price', '');
            submitBtn.appendChild(priceSpan);
          }
          if (priceStr) priceSpan.textContent = priceStr;
        } else {
          submitBtn.textContent = offLabel;
        }
      }
      if (stickyBtn) {
        if (!stickyBtn.dataset.originalText) stickyBtn.dataset.originalText = stickyBtn.textContent.trim();
        stickyBtn.disabled = state !== 'available';
        stickyBtn.textContent = state === 'available' ? stickyBtn.dataset.originalText : offLabel;
      }
      /* Announce the resulting state as one sentence. Price, availability and
         the CTA all move together here; without this a screen-reader user is
         given no indication that anything changed at all. */
      /* Only announce a CHANGE the customer made. The initial sync runs on
         load, and writing into a role="status" region then makes the page
         announce its own default variant unprompted, before any interaction. */
      const announce = document.querySelector('[data-variant-announce]');
      if (announce && announceReady) {
        const chosen = chosenValues().filter(Boolean).join(' / ');
        const status = state === 'available' ? (priceStr ? 'in stock, ' + priceStr : 'in stock')
                     : state === 'soldout' ? 'sold out'
                     : 'unavailable';
        announce.textContent = (chosen ? chosen + ' — ' : '') + status;
      }
    };

    const sync = () => {
      const chosen = chosenValues();
      const match = variants.find((v) => {
        return chosen.every((val, i) => v.options[i] === val);
      });
      if (!match) {
        // No variant for this option combo — never submit a stale variant id.
        setCtaState('unavailable');
        return;
      }
      if (hiddenId) hiddenId.value = match.id;
      const priceStr = formatMoney(match.price);
      if (priceEl) priceEl.textContent = priceStr;
      if (stickyPriceEl) stickyPriceEl.textContent = priceStr;
      syncStock(match);
      setCtaState(match.available ? 'available' : 'soldout', priceStr);
    };

    let announceReady = false;
    Array.prototype.forEach.call(controls, (c) => c.addEventListener('change', sync));
    sync();                 // initial state: silent
    announceReady = true;   // subsequent syncs are real changes, so announce

    /* Back/forward restores a page from the bfcache WITHOUT re-running
       DOMContentLoaded. The browser repopulates the visible <select> with the
       customer's previous choice, but the hidden variant id keeps whatever
       value the initial render wrote — so the form would silently post the
       wrong variant while the page shows the right one. Re-sync on restore so
       what is displayed and what is submitted cannot diverge. */
    window.addEventListener('pageshow', (e) => {
      /* e.persisted covers only the bfcache path. The reproduction that found
         this reported navType "back_forward", which is the NON-bfcache restore
         — where persisted is false and the guard would have missed it. Cover
         both, and re-sync unconditionally on restore rather than guessing. */
      let navType = '';
      try { navType = (performance.getEntriesByType('navigation')[0] || {}).type || ''; } catch (err) {}
      if (e.persisted || navType === 'back_forward') sync();
    });
  });

  /* -----------------------------------------------------
     STICKY ADD TO CART (mobile PDP)
     ----------------------------------------------------- */
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  if (stickyAtc) {
    const heroCta = document.querySelector('.sk-buy__cta-wrap');
    const stickyBtn = stickyAtc.querySelector('[data-sticky-atc-btn]');
    /* The bar is fixed to the viewport bottom, so once the footer scrolls in
       it sits permanently on top of the footer's legal row — content the
       shopper can see but never reach. Stand down as the footer arrives:
       by then the page's own closing CTA is on screen anyway. */
    const siteFooter = document.querySelector('.sandy-footer');
    const toggleSticky = () => {
      if (!heroCta) return;
      const rect = heroCta.getBoundingClientRect();
      let show = rect.bottom < 0;
      if (show && siteFooter) {
        const footerTop = siteFooter.getBoundingClientRect().top;
        const barHeight = stickyAtc.offsetHeight || 0;
        if (footerTop <= window.innerHeight - barHeight) show = false;
      }
      stickyAtc.classList.toggle('is-visible', show);
      stickyAtc.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
    };
    toggleSticky();
    window.addEventListener('scroll', toggleSticky, { passive: true });
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const mainForm = document.querySelector('[data-product-form]');
        if (!mainForm) return;
        // Bail when the main submit is disabled (sold out / unavailable combo).
        const mainSubmit = mainForm.querySelector('button[type="submit"]');
        if (mainSubmit && mainSubmit.disabled) return;
        mainForm.requestSubmit ? mainForm.requestSubmit() : mainForm.submit();
      });
    }
  }

  /* -----------------------------------------------------
     (Removed) fabricated "viewing now / bought today" counters.
     Real scarcity stays on the inventory-backed [data-stock-badge].
     ----------------------------------------------------- */

  /* -----------------------------------------------------
     TEXTURE ZOOM - accessible variation tabs (scoped to .v2-zoom)
     ----------------------------------------------------- */
  document.querySelectorAll('[data-texture-zoom]').forEach((zoom) => {
    const tabs = Array.from(zoom.querySelectorAll('[data-tz-tab]'));
    if (!tabs.length) return;
    const imgs = Array.from(zoom.querySelectorAll('[data-tz-img]'));
    const panels = Array.from(zoom.querySelectorAll('[data-tz-info]'));

    const activate = (index, withFocus) => {
      const key = String(index);
      tabs.forEach((tab) => {
        const on = tab.dataset.tzTab === key;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on && withFocus) tab.focus();
      });
      imgs.forEach((img) => {
        const on = img.dataset.tzImg === key;
        img.classList.toggle('is-active', on);
        if (on) img.removeAttribute('aria-hidden');
        else img.setAttribute('aria-hidden', 'true');
      });
      panels.forEach((panel) => {
        const on = panel.dataset.tzInfo === key;
        panel.classList.toggle('is-active', on);
        panel.toggleAttribute('hidden', !on);
      });
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab.dataset.tzTab, false));
      tab.addEventListener('keydown', (e) => {
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        activate(tabs[next].dataset.tzTab, true);
      });
    });

    /* ---- Bind these visual tabs to the REAL Shopify variant select ----
       The product form stays the single source of truth. A tab click just
       drives the matching native <select> BY VALUE (never a hardcoded id or
       option name) and dispatches `change`; the product-form `sync()` above
       then updates the hidden variant id, price, sticky price, stock + CTA.
       Tabs with no data-variant-value stay visual-only (back-compatible). */
    const tzForms = document.querySelectorAll('[data-product-form]');
    const tzFindOption = (value) => {
      const want = (value || '').trim().toLowerCase();
      if (!want) return null;
      for (const form of tzForms) {
        for (const sel of form.querySelectorAll('[data-variant-select]')) {
          const opt = Array.from(sel.options).find((o) => (o.value || '').trim().toLowerCase() === want);
          if (opt) return { sel, value: opt.value };
        }
      }
      return null;
    };
    const tzSetVariant = (value) => {
      const hit = tzFindOption(value);
      if (!hit) return;
      if (hit.sel.value !== hit.value) {
        hit.sel.value = hit.value;
        hit.sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    const tzTabByValue = (value) => {
      const want = (value || '').trim().toLowerCase();
      if (!want) return null;
      return tabs.find((t) => ((t.getAttribute('data-variant-value') || '').trim().toLowerCase() === want)) || null;
    };

    // forward: tab click -> drive the real variant select
    tabs.forEach((tab) => {
      const val = tab.getAttribute('data-variant-value');
      if (val) tab.addEventListener('click', () => tzSetVariant(val));
    });

    // reverse: real variant select change -> light up the matching tab
    tzForms.forEach((form) => {
      form.querySelectorAll('[data-variant-select]').forEach((sel) => {
        sel.addEventListener('change', () => {
          const t = tzTabByValue(sel.value);
          if (t) activate(t.dataset.tzTab, false);
        });
      });
    });

    // init: reflect the variant the form already has onto the tabs
    const tzInitSel = document.querySelector('[data-product-form] [data-variant-select]');
    if (tzInitSel) {
      const t = tzTabByValue(tzInitSel.value);
      if (t) activate(t.dataset.tzTab, false);
    }
  });

  /* -----------------------------------------------------
     TOP VARIANT CARDS (style selector near the buy area)
     Same single-source-of-truth pattern as the texture tabs:
     a card click drives the real [data-variant-select] BY VALUE
     (no second form, no hardcoded id) and dispatches `change`; the
     product-form sync above updates id/price/sticky/stock/CTA. Top
     cards + lower tabs stay in sync because both listen to that change.
     ----------------------------------------------------- */
  (() => {
    const cards = Array.from(document.querySelectorAll('[data-bundle-pick]'));
    if (!cards.length) return;
    const forms = document.querySelectorAll('[data-product-form]');
    if (!forms.length) return;

    const findOption = (value) => {
      const want = (value || '').trim().toLowerCase();
      if (!want) return null;
      for (const form of forms) {
        for (const sel of form.querySelectorAll('[data-variant-select]')) {
          const opt = Array.from(sel.options).find((o) => (o.value || '').trim().toLowerCase() === want);
          if (opt) return { sel, value: opt.value };
        }
      }
      return null;
    };
    const setVariant = (value) => {
      const hit = findOption(value);
      if (!hit) return;
      if (hit.sel.value !== hit.value) {
        hit.sel.value = hit.value;
        hit.sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    const markActive = (value) => {
      const want = (value || '').trim().toLowerCase();
      cards.forEach((card) => {
        const on = (card.getAttribute('data-variant-value') || '').trim().toLowerCase() === want;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };

    // forward: card click -> select the real variant
    cards.forEach((card) => {
      card.addEventListener('click', () => setVariant(card.getAttribute('data-variant-value')));
    });
    // reverse: any variant select change -> highlight the matching card
    forms.forEach((form) => {
      form.querySelectorAll('[data-variant-select]').forEach((sel) => {
        sel.addEventListener('change', () => markActive(sel.value));
      });
    });
    // init: reflect the variant the form already has onto the cards
    const initSel = document.querySelector('[data-product-form] [data-variant-select]');
    if (initSel) markActive(initSel.value);
  })();

  /* -----------------------------------------------------
     MULTI-BUY BUILDER (PDP) — adds the REAL selected
     variants to cart in one request via Shopify's AJAX
     endpoint. Fully scoped to [data-multibuy]; it never
     touches the main product form, variant sync, sticky
     ATC, cart page or checkout. No discount math lives
     here — the % offer is a Shopify Admin discount.
     ----------------------------------------------------- */
  document.querySelectorAll('[data-multibuy]').forEach((root) => {
    const cards = Array.from(root.querySelectorAll('[data-multibuy-item]'));
    const summary = root.querySelector('[data-multibuy-summary]');
    const addBtn = root.querySelector('[data-multibuy-add]');
    if (!cards.length || !addBtn) return;

    const labelFor = (n) => root.getAttribute('data-label-' + Math.min(n, 3)) || '';
    const selected = () => cards.filter((c) => c.classList.contains('is-on'));

    const update = () => {
      const n = selected().length;
      if (summary) {
        summary.textContent = labelFor(n);
        summary.dataset.tier = String(Math.min(n, 3));
      }
      addBtn.disabled = n === 0;
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        if (card.disabled) return;
        card.classList.toggle('is-on');
        card.setAttribute('aria-pressed', card.classList.contains('is-on') ? 'true' : 'false');
        update();
      });
    });

    addBtn.addEventListener('click', () => {
      const items = selected()
        .map((c) => ({ id: parseInt(c.dataset.variantId, 10), quantity: 1 }))
        .filter((i) => Number.isFinite(i.id));
      if (!items.length || addBtn.dataset.busy === '1') return;
      addBtn.dataset.busy = '1';
      addBtn.disabled = true;
      const originalLabel = addBtn.textContent;
      addBtn.textContent = 'Adding…';
      fetch(root.dataset.addUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('cart add failed');
          return res.json();
        })
        .then(() => {
          window.location.href = root.dataset.cartUrl || '/cart';
        })
        .catch(() => {
          addBtn.dataset.busy = '';
          addBtn.disabled = false;
          addBtn.textContent = originalLabel;
          if (summary) {
            summary.textContent = "Couldn't add — please try again.";
            summary.dataset.tier = 'err';
          }
        });
    });

    update();
  });
});
